import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api';
import { Agent } from '../types/agentTypes';
import { CreateAgentData } from '../api/modules/agentApi';
import { useParams } from 'react-router-dom';
import { useMemo } from 'react';

const AGENTS_QUERY_KEY = ['agents'];

interface UpdatePreferenceContext {
  previousAgents: Agent[] | undefined;
}

export const useAgents = () => {
  const queryClient = useQueryClient();
  const { agentId } = useParams<{ agentId: string }>();

  // Query for fetching agents
  const {
    data: agents = [],
    isLoading: isLoadingAgents,
    error: agentError,
    refetch: fetchAgents,
  } = useQuery({
    queryKey: AGENTS_QUERY_KEY,
    queryFn: apiClient.agent.fetchAgentsList,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  const currentAgent = useMemo(() => agents.find(agent => agent.id === agentId), [agents, agentId]);

  // Mutation for creating an agent
  const createAgentMutation = useMutation({
    mutationFn: (data: CreateAgentData) => apiClient.agent.createAgent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
    },
  });

  // Mutation for updating agent preference with optimistic updates
  const updatePreferenceMutation = useMutation<
    { is_preferred: boolean },
    Error,
    { agentId: string; isPreferred: boolean },
    UpdatePreferenceContext
  >({
    mutationFn: ({ agentId, isPreferred }) => apiClient.agent.updateAgentPreference(agentId, isPreferred),

    // Optimistic update - immediately update UI before API call
    onMutate: async ({ agentId, isPreferred }): Promise<UpdatePreferenceContext> => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: AGENTS_QUERY_KEY });

      // Snapshot the previous value for rollback
      const previousAgents = queryClient.getQueryData<Agent[]>(AGENTS_QUERY_KEY);

      // Optimistically update the cache with the new preference
      queryClient.setQueryData<Agent[]>(AGENTS_QUERY_KEY, (oldAgents) => {
        if (!oldAgents) return oldAgents;
        return oldAgents.map(agent =>
          agent.id === agentId
            ? { ...agent, is_preferred: isPreferred }
            : agent
        );
      });

      // Return context with previous data for potential rollback
      return { previousAgents };
    },

    // On error, rollback to previous state
    onError: (error, { agentId }, context) => {

      // Rollback to previous state if we have it
      if (context?.previousAgents) {
        queryClient.setQueryData(AGENTS_QUERY_KEY, context.previousAgents);
      }
    },

    // Always refetch after error or success to ensure consistency
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
    },
  });

  // Helper function for toggling favorites with better UX
  const toggleAgentFavorite = async (agentId: string) => {
    // Get current preference state
    const agent = agents.find(agent => agent.id === agentId);
    if (!agent) return;

    const newPreference = !agent.is_preferred;

    await updatePreferenceMutation.mutateAsync({ agentId, isPreferred: newPreference });
  };

  return {
    // Data
    agents,
    currentAgent,
    isLoadingAgents,
    agentError: agentError?.message || null,

    // Actions
    fetchAgents,
    createAgent: createAgentMutation.mutateAsync,
    toggleAgentFavorite,

    // Mutation objects (for advanced usage)
    updatePreferenceMutation,

    // Mutation states
    isCreatingAgent: createAgentMutation.isPending,
    isUpdatingPreference: updatePreferenceMutation.isPending,

    // Mutation errors
    createAgentError: createAgentMutation.error?.message || null,
    updatePreferenceError: updatePreferenceMutation.error?.message || null,
  };
};
